import { computed, ElementRef, Signal, signal } from '@angular/core';
import { ILpCanvasRendererContext } from '@features/lp/lp-canvas/lp-canvas-renderer/canvas-contexts';
import { LpCanvasElement } from '@typings/lp/generated';
import { Vector2CanvasNormalized, Vector2Screen } from '@typings/vectors';

export interface ILpCanvasElementContext {
  readonly element: Signal<LpCanvasElement>;
  readonly el: ElementRef<HTMLElement>;

  setStyle(newStyle: Partial<CSSStyleDeclaration>): this;

  clearStyle(): void;

  setHoverStyle(newStyle: Partial<CSSStyleDeclaration>): this;

  setActiveStyle(newStyle: Partial<CSSStyleDeclaration>): this;

  clearHoverStyle(): void;

  transformPosition(transformer: (current: Vector2CanvasNormalized, origin: Vector2CanvasNormalized) => Vector2CanvasNormalized): void;

  translate(position: Vector2Screen | undefined): void;

  animateBubble(enable: boolean): this;

  animatePulse(enable: boolean): this;

  requirePointerEvents(cursor?: string): this;

  transition(transition: string): this;

  setVisibility(isVisible: boolean): this;
}

export class LpCanvasElementContext implements ILpCanvasElementContext {
  public isBubbling = signal(false);
  public isPulsing = signal(false);
  public visibility = signal<'VISIBLE' | 'HIDDEN' | 'HIDDEN_INSTANT' | 'VISIBLE_INSTANT' | undefined>(undefined);
  private transitions = signal<string[]>([]);
  transitionStyle = computed(() => this.transitions().join(', '));
  // A signal holding the current style
  private _style = signal<Partial<CSSStyleDeclaration>>({});
  style = this._style.asReadonly(); // Expose as readonly
  // A signal holding the current style
  private _hoverStyle = signal<Partial<CSSStyleDeclaration>>({});
  hoverStyle = this._hoverStyle.asReadonly(); // Expose as readonly
  private _activeStyle = signal<Partial<CSSStyleDeclaration>>({});
  activeStyle = this._activeStyle.asReadonly(); // Expose as readonly

  public constructor(
    private readonly canvasCtx: Signal<ILpCanvasRendererContext>,
    public readonly element: Signal<LpCanvasElement>,
    public readonly el: ElementRef<HTMLElement>,
  ) {}

  setVisibility(isVisible: boolean): this {
    if (this.visibility() === undefined) {
      this.visibility.set(isVisible ? 'VISIBLE_INSTANT' : 'HIDDEN_INSTANT');
    } else {
      // NOTE: This is a bit of a hack to avoid flickering when the element is already in the desired state
      if ((this.visibility() === 'HIDDEN_INSTANT' && !isVisible) || (this.visibility() === 'VISIBLE_INSTANT' && isVisible)) return this;

      this.visibility.set(isVisible ? 'VISIBLE' : 'HIDDEN');
    }
    return this;
  }

  public requirePointerEvents(cursor?: string): this {
    this.setStyle({
      pointerEvents: 'initial',
      cursor,
    });
    return this;
  }

  public transition(transition: string): this {
    const name = transition.split(' ')[0];
    this.transitions.set([...this.transitions().filter((t) => !t.startsWith(name)), transition]);
    return this;
  }

  public transformPosition(
    transformer: (current: Vector2CanvasNormalized, origin: Vector2CanvasNormalized) => Vector2CanvasNormalized,
  ): void {
    this.canvasCtx().registerPositionTransformer(transformer);
  }

  public translate(position: Vector2Screen | undefined): void {
    if (position) {
      this.setStyle({ translate: `${position[0]}px ${position[1]}px` });
    } else {
      this.setStyle({ translate: `none` });
    }
  }

  public animateBubble(enable: boolean): this {
    this.isBubbling.set(enable);
    return this;
  }

  public animatePulse(enable: boolean): this {
    this.isPulsing.set(enable);
    return this;
  }

  setStyle(newStyle: Partial<CSSStyleDeclaration>): this {
    this._style.update((prev) => ({ ...prev, ...newStyle }));
    return this;
  }

  setHoverStyle(newStyle: Partial<CSSStyleDeclaration>): this {
    this._hoverStyle.update((prev) => ({ ...prev, ...newStyle }));
    return this;
  }

  setActiveStyle(newStyle: Partial<CSSStyleDeclaration>): this {
    this._activeStyle.update((prev) => ({ ...prev, ...newStyle }));
    return this;
  }

  clearHoverStyle() {
    this._hoverStyle.set({});
  }

  clearStyle() {
    this._style.set({});
  }
}
